import type { Bandeintrag, Verteilart, Verteilperson, Verteilstand, Verteilzeile, Zuteilung } from "./faelle";
import { tagImBetrieb, tagPlus, uhrImBetrieb, wochentagVon, zeitpunktLesen } from "./betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Verteilen — die Regeln hinter dem Personal-Band (53-verteilen.md).

   Seit dem 25.09.2026 wird **im Planungsbrett** verteilt, im Abschnitt
   Personal (Luca: Variante 1 aus `docs/artefakte/verteilen-im-brett.html`):
   oben eine Zeile „Noch niemand" mit allem, was frei ist, darunter jeder
   Mensch mit seinen Belegungen und seinen Aufgaben im selben Tag. Die
   eigene Seite „Verteilen" ist abgeschafft — Regel 8: eine Planungsliste,
   nicht zwei. Was sich daraus rechnen lässt, steht hier und nicht in der
   Maske — ohne React, damit es sich prüfen lässt
   (`apps/web/test/verteilen.test.ts`):

   | | |
   |---|---|
   | `aufgabenAus` | die Aufgaben aus den Zeilen der API, je mit Tag und Richtung |
   | `arbeitstagVorher` | der Tag, an dem vorbereitet wird, wenn die API ihn nicht nennt |
   | `fahrtenAm` | die Aufgaben eines Tages, gebündelt je Auftrag und Richtung |
   | `lageAm` | was ein Mensch an einem Tag schon hat — Band und Zuteilungen |
   | `hindernis` | warum ein Mensch für eine Aufgabe schlecht passt |
   | `vorschlagen` | wer die freien Aufgaben einer Fahrt nimmt — nur mit Grund (08.10.2026) |
   | `vorschlaegeFuerTage` | „Vorschläge übernehmen" über die Tage im Bild |

   **Ein Hinweis, kein Riegel.** Wer im Urlaub ist, bekommt trotzdem, was
   die Disposition ihm gibt — das Brett sagt es nur laut. Das Band und die
   Qualifikation sind Auskunft (Regel 9: keine Führerscheinkontrolle).
   ══════════════════════════════════════════════════════════════════ */

/* Zeitzone, Tag eines Zeitpunkts und Tagesrechnung stehen seit N8-02 im
   gemeinsamen Baustein `daten/betriebstag.ts`; hier unter den alten Namen. */
export { ZEITZONE, zeitpunktLesen, tagPlus } from "./betriebstag";

/** Der Kalendertag eines Zeitpunkts am Hof, als `JJJJ-MM-TT`. */
export function tagAmHof(zeitpunkt: string | Date): string {
  return tagImBetrieb(zeitpunkt);
}

/**
 * Der n-te Arbeitstag (Montag bis Freitag) vor einem Tag — dieselbe Regel
 * wie `arbeitstag_vorher` in der Datenbank (0175, Entscheidung M8c). Die API
 * nennt den Tag der Vorbereitung selbst (`tag_vorbereitung`, dort mit dem
 * Vorlauf der Maschine); diese Fassung gilt nur, wenn er fehlt.
 */
export function arbeitstagVorher(tag: string, n = 1): string {
  if (n <= 0) return tag;
  let d = tag;
  for (let rest = n; rest > 0;) {
    d = tagPlus(d, -1);
    if (wochentagVon(d) < 5) rest -= 1;
  }
  return d;
}

/**
 * Der n-te Arbeitstag ab `von` — `von` selbst ist der nullte, wenn er einer
 * ist. So rechnet die Attrappe ihre Beispielwoche auf ein Fenster, das nicht
 * am Montag beginnt (`daten/routen.ts`, `attrappe/faelle.ts`).
 */
export function werktagAb(von: string, n: number): string {
  const werktag = (d: string) => wochentagVon(d) < 5;
  let d = von;
  while (!werktag(d)) d = tagPlus(d, 1);
  for (let rest = n; rest > 0;) {
    d = tagPlus(d, 1);
    if (werktag(d)) rest -= 1;
  }
  return d;
}

/**
 * Wo das Brett beginnt, wenn eine Meldung auf `/verteilen/<Tag>` zeigt.
 *
 * **Am Tag der Meldung** (03.10.2026, Playbook M-07). Bis dahin begann es
 * heute, solange der Tag in den fünf Arbeitstagen ab heute lag — wer die
 * Meldung für Montag öffnete, landete am Samstag und suchte den Tag. Ohne
 * (gültigen) Tag beginnt es heute.
 */
export function verteilBeginn(tag: string | undefined, heute: string): string {
  if (!tag || !/^\d{4}-\d{2}-\d{2}$/.test(tag)) return heute;
  // Gibt es den Tag? `2026-02-31` rollte `Date` still in den März.
  return tagPlus(tag, 0) !== tag ? heute : tag;
}

export const uhrzeit = (zeitpunkt: string): string => uhrImBetrieb(zeitpunkt);

/** Der Schlüssel „liegt zurück": fällig vor heute und nicht erledigt. Das
 *  Brett zeichnet solche Aufgaben in die Spalte von heute. */
export const ZURUECK = "zurueck";

export type Aufgabe = {
  schluessel: string;
  belegung: string;
  art: Verteilart;
  /** Der Tag, an dem sie dran ist — oder `ZURUECK`, wenn sie liegen geblieben ist. */
  tag: string;
  /** Wann: Beginn bei Vorbereiten und Übergeben, Ende beim Zurücknehmen. */
  zeit: string;
  richtung: "hinaus" | "herein";
  /** Der Kunde holt ab oder bringt zurück — dann ist es Arbeit am Hof, keine Fahrt. */
  amHof: boolean;
  kennung: string;
  typ: string | null;
  vorgang: string | null;
  kunde: string | null;
  ort: string | null;
  ortschaft: string | null;
  entfernung: number | null;
  verlangt: string[];
  wer: { id: string; name: string } | null;
  zugeteiltVon: string | null;
  erledigt: boolean;
  /** Ein Fahrer oder Bediener ist an der Position gebucht — die Aufgabe ist seine (M8). */
  fest: boolean;
  /** Die Bediener des Einsatzes (Ids) -- nur sie brauchen den Schein (E17). */
  bediener: string[];
};

/**
 * Die Aufgaben aus den Zeilen der API.
 *
 * **Vorbereiten steht am Arbeitstag vor der Übergabe** (Luca, 25.09.2026):
 * am Tag, den die API als `tag_vorbereitung` nennt — die Regel der Maschine,
 * in der Vorgabe der letzte Arbeitstag davor. Übergeben steht am Tag des
 * Beginns, Zurücknehmen am Tag des Endes. Was vor `heute` liegt und nicht
 * erledigt ist, steht unter `ZURUECK`; was vor `heute` liegt und erledigt
 * ist, gar nicht mehr.
 */
export function aufgabenAus(zeilen: Verteilzeile[], heute: string): Aufgabe[] {
  const liste: Aufgabe[] = [];
  for (const z of zeilen) {
    const bediener = z.bediener[0] ?? null;
    const basis = {
      belegung: z.belegung_id, kennung: z.kennung, typ: z.typ, vorgang: z.vorgang, kunde: z.kunde,
      ort: z.ort ?? null, ortschaft: z.ortschaft ?? null, entfernung: z.entfernung ?? null,
      verlangt: z.verlangt ?? [], amHof: z.uebergabeart === "abholen", fest: bediener !== null,
      bediener: z.bediener.map((b) => b.id),
    };
    const hinzu = (art: Verteilart, tag: string, zeit: string, erledigt: boolean,
      wer: { id: string; name: string } | null, zugeteiltVon: string | null) => {
      if (tag < heute && erledigt) return;
      liste.push({
        ...basis, schluessel: `${z.belegung_id}:${art}`, art, zeit, erledigt,
        tag: tag < heute ? ZURUECK : tag,
        richtung: art === "ruecknahme" ? "herein" : "hinaus",
        wer: bediener ?? wer, zugeteiltVon: bediener ? null : zugeteiltVon,
      });
    };
    if (!z.ausgegeben) {
      hinzu("vorbereitung", z.tag_vorbereitung ?? arbeitstagVorher(z.tag_beginn), z.beginn,
        z.vorbereitung.erledigt, z.vorbereitung.wer, z.vorbereitung.zugeteilt_von);
      hinzu("uebergabe", z.tag_beginn, z.beginn, false, z.uebergabe.wer, z.uebergabe.zugeteilt_von);
    } else if (z.tag_beginn >= heute) {
      hinzu("uebergabe", z.tag_beginn, z.beginn, true, z.uebergabe.wer, z.uebergabe.zugeteilt_von);
    }
    if (z.ausgegeben || z.tag_ende >= heute) {
      hinzu("ruecknahme", z.tag_ende, z.ende, z.zurueck, z.ruecknahme.wer, z.ruecknahme.zugeteilt_von);
    }
  }
  return liste.sort((a, b) => zeitpunktLesen(a.zeit).getTime() - zeitpunktLesen(b.zeit).getTime()
    || a.kennung.localeCompare(b.kennung) || RANG[a.art] - RANG[b.art]);
}

const RANG: Record<Verteilart, number> = { vorbereitung: 0, uebergabe: 1, ruecknahme: 2 };

/** Frei heißt: nicht erledigt, niemand hat sie, und sie hängt an keinem Fahrer. */
export const istFrei = (a: Aufgabe): boolean => !a.erledigt && !a.fest && a.wer === null;
/** Umhängen lässt sich, was nicht erledigt ist und an keinem Fahrer hängt. */
export const istBeweglich = (a: Aufgabe): boolean => !a.erledigt && !a.fest;

/**
 * **Übergeben und Zurückholen des Bedieners sind Teil seines Einsatzes**
 * (Luca, 25.09.2026: *„als Fahrer ist die Aufgabe ja nur ein Teil davon …
 * im Rahmen des Bedieners"*). Der Kranfahrer bringt den Kran und holt ihn
 * wieder; das steht schon als sein Balken im Band. Als eigene Marke daneben
 * wäre es dieselbe Arbeit zweimal. Das Vorbereiten bleibt eine Marke — das
 * macht der Hof am Vortag, nicht zwingend der Fahrer.
 */
export const imEinsatzEnthalten = (a: Aufgabe): boolean => a.fest && a.art !== "vorbereitung";

/** Eine Fahrt: die Aufgaben eines Auftrags an einem Tag in einer Richtung. */
export type Fahrt = {
  schluessel: string;
  tag: string;
  richtung: "hinaus" | "herein";
  zeit: string;
  vorgang: string | null;
  kunde: string | null;
  ort: string | null;
  ortschaft: string | null;
  entfernung: number | null;
  amHof: boolean;
  /** Je Gerät die Aufgaben, in der Reihenfolge Vorbereiten, Übergeben, Zurücknehmen. */
  geraete: { belegung: string; kennung: string; typ: string | null; aufgaben: Aufgabe[] }[];
  aufgaben: Aufgabe[];
};

/**
 * Die Aufgaben eines Tages, gebündelt je Auftrag, Richtung und Art der
 * Übergabe — was zusammen hinausgeht, fährt meist einer (Regel 11: das
 * Bündel ist abgeleitet, niemand muss es anlegen).
 */
export function fahrtenAm(aufgaben: Aufgabe[], tag: string): Fahrt[] {
  const karte = new Map<string, Fahrt>();
  for (const a of aufgaben) {
    if (a.tag !== tag) continue;
    const schluessel = `${a.tag}|${a.vorgang ?? a.belegung}|${a.richtung}|${a.amHof ? "hof" : "weg"}`;
    let f = karte.get(schluessel);
    if (!f) {
      f = {
        schluessel, tag: a.tag, richtung: a.richtung, zeit: a.zeit, vorgang: a.vorgang, kunde: a.kunde,
        ort: a.ort, ortschaft: a.ortschaft, entfernung: a.entfernung, amHof: a.amHof, geraete: [], aufgaben: [],
      };
      karte.set(schluessel, f);
    }
    if (zeitpunktLesen(a.zeit) < zeitpunktLesen(f.zeit)) f.zeit = a.zeit;
    f.aufgaben.push(a);
    let g = f.geraete.find((x) => x.belegung === a.belegung);
    if (!g) {
      g = { belegung: a.belegung, kennung: a.kennung, typ: a.typ, aufgaben: [] };
      f.geraete.push(g);
    }
    g.aufgaben.push(a);
  }
  return [...karte.values()].sort((a, b) =>
    zeitpunktLesen(a.zeit).getTime() - zeitpunktLesen(b.zeit).getTime() || (a.vorgang ?? "").localeCompare(b.vorgang ?? ""));
}

/* ══════════════ Die Lage eines Menschen an einem Tag ══════════════ */

const ABWESEND = new Set(["urlaub", "krank", "schulung", "sperre"]);

export type Lage = {
  /** Urlaub, krank, Schulung — den ganzen Tag oder einen Teil davon. */
  abwesend: Bandeintrag | null;
  /** Einsätze als Fahrer oder Bediener und Termine, die in den Tag fallen. */
  einsaetze: Bandeintrag[];
  /** Wie viele offene Aufgaben er an diesem Tag schon hat. */
  aufgaben: number;
};

function bandAmTag(e: Bandeintrag, tag: string): boolean {
  const bis = new Date(zeitpunktLesen(e.bis).getTime() - 1);
  return tagAmHof(e.von) <= tag && tagAmHof(bis) >= tag;
}

export function lageAm(person: string, tag: string, band: Bandeintrag[], aufgaben: Aufgabe[]): Lage {
  const hier = tag === ZURUECK ? [] : band.filter((e) => e.person_id === person && bandAmTag(e, tag));
  return {
    abwesend: hier.find((e) => ABWESEND.has(e.art)) ?? null,
    einsaetze: hier.filter((e) => !ABWESEND.has(e.art)),
    aufgaben: aufgaben.filter((a) => a.tag === tag && !a.erledigt && a.wer?.id === person).length,
  };
}

/* ══════════════ Was gegen eine Zuteilung spricht ══════════════ */

export type Hindernis =
  | { art: "abwesend"; grund: string; stufe: "stopp" }
  | { art: "einsatz"; kennung: string | null; von: string; bis: string; stufe: "acht" }
  | { art: "termin"; von: string; bis: string; stufe: "acht" }
  | { art: "qualifikation"; codes: string[]; stufe: "acht" };

/** Wie weit vor und nach dem Zeitpunkt einer Übergabe der Mensch gebraucht wird. */
const FENSTER_MS = 90 * 60_000;

/**
 * Warum `person` für `aufgabe` schlecht passt — leer, wenn nichts dagegen spricht.
 *
 * - **abwesend**: Urlaub, krank, Schulung an diesem Tag.
 * - **einsatz / termin**: Er fährt zur selben Zeit woanders (± 90 Minuten
 *   um die Übergabe) — nicht beim Vorbereiten, das geht am Vortag. Der
 *   Einsatz am selben Auftrag zählt nicht: Da fährt er ohnehin hin.
 * - **qualifikation**: Das Gerät verlangt etwas, das er nicht (mehr) hat —
 *   nur beim Übergeben und Zurücknehmen, und nur, wenn er der Bediener des
 *   Einsatzes ist (Entscheidung E17 vom 04.10.2026: Ausliefern braucht den
 *   Schein nicht, Bedienen schon).
 */
export function hindernis(aufgabe: Aufgabe, person: Verteilperson, band: Bandeintrag[]): Hindernis[] {
  const liste: Hindernis[] = [];
  if (aufgabe.tag !== ZURUECK) {
    const lage = lageAm(person.id, aufgabe.tag, band, []);
    if (lage.abwesend) liste.push({ art: "abwesend", grund: lage.abwesend.art, stufe: "stopp" });
    if (aufgabe.art !== "vorbereitung") {
      const t = zeitpunktLesen(aufgabe.zeit).getTime();
      for (const e of lage.einsaetze) {
        if (e.vorgang && e.vorgang === aufgabe.vorgang) continue;
        const von = zeitpunktLesen(e.von).getTime();
        const bis = zeitpunktLesen(e.bis).getTime();
        if (von < t + FENSTER_MS && bis > t - FENSTER_MS) {
          liste.push(e.art === "termin"
            ? { art: "termin", von: e.von, bis: e.bis, stufe: "acht" }
            : { art: "einsatz", kennung: e.kennung, von: e.von, bis: e.bis, stufe: "acht" });
        }
      }
    }
  }
  if (aufgabe.art !== "vorbereitung" && aufgabe.verlangt.length && aufgabe.bediener.includes(person.id)) {
    const tag = aufgabe.tag === ZURUECK ? tagAmHof(new Date()) : aufgabe.tag;
    const fehlt = aufgabe.verlangt.filter((code) => !person.qualifikationen.some(
      (q) => q.code === code && (q.gueltig_bis === null || q.gueltig_bis >= tag)));
    if (fehlt.length) liste.push({ art: "qualifikation", codes: fehlt, stufe: "acht" });
  }
  return liste;
}

/** Die schwerste Stufe einer Liste von Hindernissen. */
export function stufeVon(liste: Hindernis[]): "stopp" | "acht" | null {
  if (liste.some((h) => h.stufe === "stopp")) return "stopp";
  return liste.length ? "acht" : null;
}

/* ══════════════ Der Vorschlag ══════════════ */

/**
 * Warum jemand vorgeschlagen wird — es steht am Vorschlag (Luca, 08.10.2026:
 * *„Schlägt bei jedem immer ‚Fahrer' vor, warum, versteht man optisch
 * nicht."*).
 *
 * | | |
 * |---|---|
 * | `auftrag` | hat an dem Tag schon etwas vom selben Auftrag — auch als Bediener im Einsatz |
 * | `qualifikation` | hat als Einziger, was verlangt ist |
 * | `einzig` | ist als Einziger zur selben Zeit frei |
 * | `last` | hat deutlich weniger zu tun als alle anderen (mindestens zwei weniger) |
 */
export type Vorschlagsgrund = "auftrag" | "qualifikation" | "einzig" | "last";
export type Vorschlag = { person: Verteilperson; grund: Vorschlagsgrund };

/** Um so viel muss einer weniger haben, damit „am wenigsten zu tun" ein Grund ist. */
export const LAST_ABSTAND = 2;

/**
 * Die Wahl aus Kandidaten mit Last — **nur, wenn es einen Grund gibt**. Bei
 * echtem Gleichstand kein Name: Ein Vorschlag, der nur der Erste im Alphabet
 * ist, ist keiner. Gemeinsam für Aufgaben und Einsätze.
 */
export function waehleMitGrund(
  kandidaten: { p: Verteilperson; last: number; frei: boolean; auftrag: boolean; qualifiziert: boolean }[],
  mitQualifikation: boolean,
): Vorschlag | null {
  const leichteste = (liste: typeof kandidaten) =>
    [...liste].sort((a, b) => a.last - b.last || Number(b.p.stamm) - Number(a.p.stamm))[0]!.p;
  const vomAuftrag = kandidaten.filter((k) => k.auftrag);
  if (vomAuftrag.length) return { person: leichteste(vomAuftrag), grund: "auftrag" };
  const frei = kandidaten.filter((k) => k.frei);
  if (mitQualifikation) {
    const koennen = frei.filter((k) => k.qualifiziert);
    if (koennen.length === 1) return { person: koennen[0]!.p, grund: "qualifikation" };
  }
  if (frei.length === 1 && kandidaten.length > 1) return { person: frei[0]!.p, grund: "einzig" };
  if (frei.length >= 2) {
    const reihe = [...frei].sort((a, b) => a.last - b.last);
    if (reihe[0]!.last + LAST_ABSTAND <= reihe[1]!.last) return { person: reihe[0]!.p, grund: "last" };
  }
  return null;
}

/**
 * **Wer die freien Aufgaben dieser Fahrt nimmt — und warum.**
 *
 * Kein Automat, der verteilt — ein Name mit Grund auf einem Knopf, den die
 * Disposition drückt oder nicht. Abwesende fallen heraus. Dann, in dieser
 * Reihenfolge: **derselbe Auftrag** an dem Tag (eine Fahrt, ein Fahrer; dazu
 * zählt der Bediener im Einsatz), **als Einziger mit der Qualifikation**,
 * **als Einziger frei** zur Zeit der Übergabe, **deutlich am wenigsten zu
 * tun**. Gibt es keinen Grund, gibt es keinen Vorschlag.
 *
 * `last` zählt, was in diesem Durchgang schon vorgeschlagen ist — so
 * verteilt „Vorschläge übernehmen" nicht alles auf denselben.
 */
export function vorschlagen(
  fahrt: Pick<Fahrt, "tag" | "vorgang" | "aufgaben">,
  stand: { leute: Verteilperson[]; band: Bandeintrag[] },
  alle: Aufgabe[],
  last: Map<string, number> = new Map(),
): Vorschlag | null {
  const frei = fahrt.aufgaben.filter(istFrei);
  if (!frei.length) return null;
  const verlangt = [...new Set(frei.filter((a) => a.art !== "vorbereitung").flatMap((a) => a.verlangt))];
  const tag = fahrt.tag === ZURUECK ? tagAmHof(new Date()) : fahrt.tag;
  const kandidaten = stand.leute.flatMap((p) => {
    const hindernisse = frei.flatMap((a) => hindernis(a, p, stand.band));
    if (hindernisse.some((h) => h.art === "abwesend")) return [];
    const lage = lageAm(p.id, fahrt.tag, stand.band, alle);
    const auftrag = fahrt.vorgang !== null && (
      alle.some((a) => a.tag === fahrt.tag && a.vorgang === fahrt.vorgang && a.wer?.id === p.id)
      || lage.einsaetze.some((e) => e.vorgang === fahrt.vorgang));
    const qualifiziert = verlangt.every((code) => p.qualifikationen.some(
      (q) => q.code === code && (q.gueltig_bis === null || q.gueltig_bis >= tag)));
    return [{
      p, auftrag, qualifiziert,
      frei: !hindernisse.some((h) => h.art === "einsatz" || h.art === "termin"),
      last: lage.aufgaben + lage.einsaetze.length + (last.get(p.id) ?? 0),
    }];
  });
  return waehleMitGrund(kandidaten, verlangt.length > 0);
}

/** Die Vorschläge für alle Fahrten eines Tages, nacheinander gewogen. */
export function vorschlaegeAm(
  fahrten: Fahrt[],
  stand: Pick<Verteilstand, "leute" | "band">,
  alle: Aufgabe[],
): Map<string, Vorschlag> {
  const last = new Map<string, number>();
  const ergebnis = new Map<string, Vorschlag>();
  for (const f of fahrten) {
    const v = vorschlagen(f, stand, alle, last);
    if (!v) continue;
    ergebnis.set(f.schluessel, v);
    last.set(v.person.id, (last.get(v.person.id) ?? 0) + f.aufgaben.filter(istFrei).length);
  }
  return ergebnis;
}

/**
 * **„Vorschläge übernehmen" für die Tage im Bild** — je Tag die Fahrten,
 * je Fahrt der Vorschlag (nur mit Grund), alles als eine Liste von
 * Zuteilungen. Gewogen wird je Tag für sich.
 */
export function vorschlaegeFuerTage(
  tage: string[],
  stand: Pick<Verteilstand, "leute" | "band">,
  alle: Aufgabe[],
): Zuteilung[] {
  return tage.flatMap((tag) => {
    const fahrten = fahrtenAm(alle, tag);
    const vorschlaege = vorschlaegeAm(fahrten, stand, alle);
    return fahrten.flatMap((f) => {
      const v = vorschlaege.get(f.schluessel);
      return v ? zuteilungenFuer(f.aufgaben.filter(istFrei), v.person.id).hin : [];
    });
  });
}

/** Die Zuteilungen für diese Aufgaben an `wer` — und die, die sie zurücknehmen. */
export function zuteilungenFuer(aufgaben: Aufgabe[], wer: string | null): { hin: Zuteilung[]; zurueck: Zuteilung[] } {
  const bewegt = aufgaben.filter((a) => istBeweglich(a) && (a.wer?.id ?? null) !== wer);
  return {
    hin: bewegt.map((a) => ({ belegung_id: a.belegung, art: a.art, wer })),
    zurueck: bewegt.map((a) => ({ belegung_id: a.belegung, art: a.art, wer: a.wer?.id ?? null })),
  };
}

/** Zwei Buchstaben für den Kreis, wenn der Betrieb kein Kürzel gepflegt hat. */
export function kuerzelVon(p: { name: string; kuerzel?: string | null }): string {
  if (p.kuerzel) return p.kuerzel.slice(0, 3).toUpperCase();
  const teile = p.name.trim().split(/\s+/);
  return ((teile[0]?.[0] ?? "") + (teile.length > 1 ? teile[teile.length - 1]![0] ?? "" : teile[0]?.[1] ?? ""))
    .toUpperCase();
}

/** Der Vorname — auf dem Kärtchen reicht er. */
export const vorname = (name: string): string => name.trim().split(/\s+/)[0] ?? name;
